import {ReloadOutlined} from '@ant-design/icons';
import {Checkbox, Col, Flex, Button, Form, Input, Row, Select} from 'antd';
import React, {useState} from 'react';

import Loader from '../../../components/Loader/Loader';
import PageContent from '../../../components/PageContent/PageContent';
import MainTableTitle from '../../../components/Table/Utils/MainTableTitle';
import {reportInitialValues} from '../constants/pcfDefaultValues';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  REPORT_TYPES,
  REPORT_LANGUAGES,
  VERIFICATION_STATUSES,
  EXPORT_FORMATS,
  SHARE_OPTIONS,
  PUBLISH_REGISTRY_VALUE,
  VERIFIED_THIRD_PARTY_VALUES
} from '../constants/pcfOptions';
import usePCFPersist from '../hooks/usePCFPersist';
import {usePCFStepHydration} from '../hooks/usePCFStepHydration';
import {ReportExportKeys, ReportExportLabels} from '../PCFKeyLabels';

import FileUpload from './FileUpload';
import PCFFooterBackButton from './PCFFooterBackButton';
import PCFFooterPrimaryButton from './PCFFooterPrimaryButton';
import {runPCFDraftSave} from './pcfFormDraft';
import PCFFormHeader from './PCFFormHeader';
import ReportModal from './ReportMdoal';
import TabStepper from './TabStepper';
import Toggle from './Toggle';

const reportValidateTrigger: Array<'onSubmit'> = ['onSubmit'];

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="M8 11V7"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <circle cx="8" cy="5" r="0.7" fill="currentColor" />
  </svg>
);

const PCFReportExport: React.FC = () => {
  const [form] = Form.useForm();
  const [submitFailed, setSubmitFailed] = useState(false);
  const [modalState, setModalState] = useState('idle');
  const reportId = 'BC-PCF-2026-579555';

  const {
    recordId,
    isStepReady,
    navigateToStep,
    reloadFromServer
  } = usePCFStepHydration(form, 'report');
  const {isSaving, saveDraftLabel, actionLabel, persist, runWhileSaving} =
    usePCFPersist();

  const persistReport = async (status: 'draft' | 'approved'): Promise<void> => {
    await persist({
      section: 'report',
      sectionValues: form.getFieldsValue(true),
      status
    });
  };

  const handleSaveDraft = (): void => {
    runWhileSaving(async () => {
      setSubmitFailed(false);
      await runPCFDraftSave(form, async () => {
        await persistReport('draft');
      });
    }).catch(() => {});
  };

  const handleGenerate = (): void => {
    runWhileSaving(async () => {
      await form.validateFields();
      setSubmitFailed(false);
      await persistReport('approved');
      setModalState('preparing');
      setTimeout(() => {
        setModalState('success');
      }, 2000);
    }).catch(() => {
      setSubmitFailed(true);
      setTimeout(() => {
        document
          .querySelector('.ant-form-item-has-error, .field-error')
          ?.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 50);
    });
  };

  const handleCloseModal = () => {
    setModalState('idle');
  };
  const handleDownload = () => {
    setModalState('idle');
  };

  const showFormErrorSummary =
    submitFailed && form.getFieldsError().some(({errors}) => errors.length > 0);

  return (
    <Flex
      vertical
      justify="space-between"
      align="center"
      gap={24}
      style={{height: '100%'}}
    >
      <Flex
        vertical={false}
        justify="space-between"
        align="center"
        style={{width: '100%'}}
      >
        <MainTableTitle
          title="Product Carbon Footprint (PCF)"
          showDivider={false}
        />
        <Flex vertical={false} gap={10} align="center" justify="center">
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => {
              reloadFromServer().catch(() => {});
            }}
            disabled={isSaving}
          />
          <Button
            type="primary"
            htmlType="button"
            onClick={handleSaveDraft}
            loading={isSaving}
            disabled={isSaving}
          >
            {saveDraftLabel}
          </Button>
        </Flex>
      </Flex>
      <TabStepper
        activeStep={5}
        completedSteps={[1, 2, 3, 4]}
        recordId={recordId}
      />
      <PageContent>
        <div className="pcf-report-export-page">
          {!isStepReady ? (
            <Loader topMargin />
          ) : (
            <>
              <PCFFormHeader
                title="5 . Report Generation & Export"
                description="Configure report output, export formats, verification, and sharing."
              />
              <section className="card">
                <div className="card-body">
                  <Form
                    form={form}
                    layout="vertical"
                    initialValues={reportInitialValues}
                    validateTrigger={reportValidateTrigger}
                    onValuesChange={() => {
                      if (submitFailed) setSubmitFailed(false);
                    }}
                  >
                    <Row gutter={rowGutter}>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item
                          name={ReportExportKeys.REPORT_TYPE}
                          label={ReportExportLabels.REPORT_TYPE}
                          rules={[{required: true, message: 'Required'}]}
                          validateFirst
                          hasFeedback
                        >
                          <Select
                            className="select"
                            placeholder="Select"
                            style={{width: '100%'}}
                            options={REPORT_TYPES}
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item
                          name={ReportExportKeys.REPORT_LANGUAGE}
                          label={ReportExportLabels.REPORT_LANGUAGE}
                          rules={[{required: true, message: 'Required'}]}
                          validateFirst
                          hasFeedback
                        >
                          <Select
                            className="select"
                            placeholder="Select"
                            style={{width: '100%'}}
                            options={REPORT_LANGUAGES}
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item label={ReportExportLabels.COMPANY_LOGO}>
                          <FileUpload />
                        </Form.Item>
                      </Col>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item
                          name={ReportExportKeys.VERIFICATION_STATUS}
                          label={ReportExportLabels.VERIFICATION_STATUS}
                          rules={[{required: true, message: 'Required'}]}
                          validateFirst
                          hasFeedback
                        >
                          <Select
                            className="select"
                            placeholder="Select"
                            style={{width: '100%'}}
                            options={VERIFICATION_STATUSES}
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item
                          name={ReportExportKeys.SIGNATORY_NAME}
                          label={ReportExportLabels.SIGNATORY_NAME}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: 'Required'
                            }
                          ]}
                          validateFirst
                          hasFeedback
                        >
                          <Input className="input" maxLength={100} />
                        </Form.Item>
                      </Col>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item
                          name={ReportExportKeys.SIGNATORY_DESIGNATION}
                          label={ReportExportLabels.SIGNATORY_DESIGNATION}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: 'Required'
                            }
                          ]}
                          validateFirst
                          hasFeedback
                        >
                          <Input className="input" maxLength={100} />
                        </Form.Item>
                      </Col>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item
                          name={ReportExportKeys.THIRD_PARTY_VERIFICATION}
                          label={ReportExportLabels.THIRD_PARTY_VERIFICATION}
                          rules={[{required: true, message: 'Required'}]}
                          validateFirst
                          hasFeedback
                        >
                          <Select
                            className="select"
                            placeholder="Select"
                            style={{width: '100%'}}
                            options={VERIFICATION_STATUSES}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div className="toggle-row">
                      <div className="toggle-label">
                        <span>Include Detailed Inventory in Report?</span>
                        <InfoIcon />
                      </div>
                      <Form.Item
                        name="includeInventory"
                        valuePropName="checked"
                        noStyle
                      >
                        <Toggle />
                      </Form.Item>
                    </div>

                    <div className="export-format-block">
                      <Form.Item
                        label={ReportExportLabels.EXPORT_FORMAT}
                        required
                        name={ReportExportKeys.EXPORT_FORMAT}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (
                                !value ||
                                !Object.values(value).some(Boolean)
                              ) {
                                return await Promise.reject(
                                  new Error('Select at least one export format')
                                );
                              }

                              await Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <div className="format-checkboxes">
                          {EXPORT_FORMATS.map((fmt) => (
                            <Form.Item
                              key={fmt.value}
                              name={['formats', fmt.value]}
                              valuePropName="checked"
                              noStyle
                            >
                              <Checkbox>{fmt.label}</Checkbox>
                            </Form.Item>
                          ))}
                        </div>
                      </Form.Item>
                    </div>

                    <Row gutter={rowGutter}>
                      <Col
                        className={PCF_COL_GUTTER_CLASS}
                        span={PCF_COL_SPAN_HALF}
                      >
                        <Form.Item
                          name={ReportExportKeys.SHARE_PUBLISH}
                          label={ReportExportLabels.SHARE_PUBLISH}
                          dependencies={['verificationStatus', 'shareStudy']}
                          rules={[
                            {required: true, message: 'Required'},
                            {
                              validator: async (_, value) => {
                                const verification =
                                  form.getFieldValue('verificationStatus');
                                if (
                                  value === PUBLISH_REGISTRY_VALUE &&
                                  !VERIFIED_THIRD_PARTY_VALUES.includes(
                                    verification
                                  )
                                ) {
                                  return await Promise.reject(
                                    new Error(
                                      'Publishing to the public registry requires "Verified by third party" status.'
                                    )
                                  );
                                }

                                await Promise.resolve();
                              }
                            }
                          ]}
                          validateFirst
                          hasFeedback
                        >
                          <Select
                            className="select"
                            placeholder="Select"
                            style={{width: '100%'}}
                            options={SHARE_OPTIONS}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </section>

              {showFormErrorSummary && (
                <div className="form-error-summary">
                  Please complete all mandatory fields marked with * before
                  generating the report.
                </div>
              )}

              <div className="footer-actions">
                <PCFFooterBackButton
                  disabled={isSaving}
                  onClick={() => {
                    navigateToStep('resultsInterpretation');
                  }}
                />
                <PCFFooterPrimaryButton
                  onClick={handleGenerate}
                  label={actionLabel('Generate & Download Report')}
                  isSaving={isSaving}
                />
              </div>
              <ReportModal
                open={modalState !== 'idle'}
                variant={modalState === 'success' ? 'success' : 'preparing'}
                reportId={reportId}
                onClose={handleCloseModal}
                onDownload={handleDownload}
              />
            </>
          )}
        </div>
      </PageContent>
    </Flex>
  );
};

export default PCFReportExport;
